import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTeamPortalAuth } from '../../contexts/TeamPortalAuthContext'
import { Users, Loader2, CheckCircle, XCircle, ArrowLeft } from 'lucide-react'

const API_URL = process.env.REACT_APP_BACKEND_URL || ''

export default function JoinTeamWithCode() {
  const { token, user } = useTeamPortalAuth()
  const navigate = useNavigate()
  const [code, setCode] = useState('')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null) // { success: bool, message: string, teamName?: string }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!code.trim()) return

    setLoading(true)
    setResult(null)

    try {
      const res = await fetch(`${API_URL}/api/invite-codes/join`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Environment': 'production'
        },
        body: JSON.stringify({
          code: code.trim().toUpperCase(),
          message: message.trim() || null
        })
      })

      const data = await res.json()

      if (res.ok) {
        setResult({
          success: true,
          message: data.message || 'Your request has been submitted!',
          teamName: data.team_name,
          status: data.status
        })
        setCode('')
        setMessage('')
      } else {
        setResult({
          success: false,
          message: data.detail || 'Failed to process invite code'
        })
      }
    } catch (err) {
      console.error('Error joining team:', err)
      setResult({
        success: false,
        message: 'An error occurred. Please try again.'
      })
    } finally {
      setLoading(false)
    }
  }

  // Format code input (uppercase, max 6 chars)
  const handleCodeChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6)
    setCode(value)
  }

  return (
    <div className="min-h-screen bg-zinc-900 flex flex-col">
      {/* Header */}
      <div className="bg-zinc-800 border-b border-zinc-700">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          <button 
            onClick={() => navigate('/portal')}
            className="p-2 rounded-lg hover:bg-zinc-800 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 text-zinc-300" />
          </button>
          <h1 className="text-lg font-semibold text-white">Join a Team</h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Result Card */}
          {result && (
            <div 
              className={`mb-6 p-4 rounded-xl border ${
                result.success 
                  ? 'bg-green-500/10 border-green-200' 
                  : 'bg-red-500/10 border-red-200'
              }`}
              data-testid="result-card"
            >
              <div className="flex items-start gap-3">
                {result.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <p className={`font-medium ${result.success ? 'text-green-800' : 'text-red-800'}`}>
                    {result.success ? 'Request Submitted!' : 'Error'}
                  </p>
                  <p className={`text-sm mt-1 ${result.success ? 'text-green-400' : 'text-red-400'}`}>
                    {result.message}
                  </p>
                  {result.success && result.status === 'pending' && (
                    <p className="text-sm mt-2 text-green-600">
                      A team admin will review your request shortly.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Join Form Card */}
          <div className="bg-zinc-800 rounded-2xl border border-zinc-700 overflow-hidden">
            <div className="p-6 border-b border-zinc-700">
              <div className="w-14 h-14 rounded-xl bg-black flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white text-center">Enter Invite Code</h2>
              <p className="text-zinc-400 text-center text-sm mt-2">
                Enter the 6-digit code shared by your team admin to request to join the team.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Code Input */}
              <div>
                <label htmlFor="invite-code" className="block text-sm font-medium text-zinc-200 mb-2">
                  Invite Code
                </label>
                <input
                  id="invite-code"
                  type="text"
                  value={code}
                  onChange={handleCodeChange}
                  placeholder="ABC123"
                  maxLength={6}
                  className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border border-zinc-700 rounded-xl focus:ring-2 focus:ring-black focus:border-black outline-none transition-all uppercase"
                  disabled={loading}
                  data-testid="invite-code-input"
                />
                <p className="text-xs text-zinc-500 mt-1.5 text-center">
                  Code is not case-sensitive
                </p>
              </div>

              {/* Optional Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-zinc-200 mb-2">
                  Message to Team Admin <span className="text-zinc-500 font-normal">(optional)</span>
                </label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Hi, I'm the parent of..."
                  rows={2}
                  className="w-full px-4 py-3 border border-zinc-700 rounded-xl focus:ring-2 focus:ring-black focus:border-black outline-none transition-all resize-none"
                  disabled={loading}
                  maxLength={200}
                  data-testid="message-input"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || code.length < 6}
                className="w-full py-3.5 bg-black text-white font-semibold rounded-xl hover:bg-zinc-700 disabled:bg-zinc-600 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                data-testid="submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  'Request to Join'
                )}
              </button>
            </form>
          </div>

          {/* My Requests Link */}
          <div className="mt-4 text-center">
            <button
              onClick={() => navigate('/portal/my-requests')}
              className="text-sm text-zinc-400 hover:text-zinc-200 underline"
              data-testid="view-requests-link"
            >
              View my pending requests
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
